import React, { useState, useEffect, useRef } from 'react';
import './CustomDatePicker.css';

const CustomDatePicker = ({ isOpen, onClose, selectedDate, onDateChange }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const pickerRef = useRef(null);

  useEffect(() => {
    if (selectedDate) {
      setCurrentMonth(new Date(selectedDate + 'T00:00:00'));
    }
  }, [selectedDate]);

  useEffect(() => {
    if (isOpen && pickerRef.current) {
      pickerRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const today = new Date();
  // Use local time components to avoid timezone issues
  const maxDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const selectedDateObj = selectedDate ? new Date(selectedDate + 'T00:00:00') : null;

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  const handleDateClick = (day) => {
    if (day === null) return;
    
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const date = new Date(year, month, day);
    // Format using local date parts - toISOString() converts to UTC and
    // shifts the date back one day for timezones ahead of UTC
    const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    // Check if date is in the future
    if (date > maxDate) return;
    
    onDateChange(dateString);
    onClose();
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    // Don't allow going to future months
    if (nextMonth <= new Date(maxDate.getFullYear(), maxDate.getMonth() + 1, 1)) {
      setCurrentMonth(nextMonth);
    }
  };

  const isToday = (day) => {
    if (day === null) return false;
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (day) => {
    if (day === null || !selectedDateObj) return false;
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return date.toDateString() === selectedDateObj.toDateString();
  };

  const isDisabled = (day) => {
    if (day === null) return true;
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return date > maxDate;
  };

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  const days = getDaysInMonth(currentMonth);
  const canGoNext = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1) <= new Date(maxDate.getFullYear(), maxDate.getMonth() + 1, 1);

  return (
    <>
      <div className="custom-date-picker-backdrop" onClick={onClose} />
      <div className="custom-date-picker" ref={pickerRef} tabIndex={-1}>
        <div className="custom-date-picker-header">
          <button 
            className="custom-date-picker-nav" 
            onClick={handlePrevMonth}
            aria-label="Mês anterior"
          >
            ‹
          </button>
          <div className="custom-date-picker-month-year">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </div>
          <button 
            className="custom-date-picker-nav" 
            onClick={handleNextMonth}
            disabled={!canGoNext}
            aria-label="Próximo mês"
          >
            ›
          </button>
        </div>
        <div className="custom-date-picker-weekdays">
          {weekDays.map(day => (
            <div key={day} className="custom-date-picker-weekday">{day}</div>
          ))}
        </div>
        <div className="custom-date-picker-days">
          {days.map((day, index) => (
            <button
              key={index}
              className={`custom-date-picker-day ${
                day === null ? 'empty' : ''
              } ${
                isToday(day) ? 'today' : ''
              } ${
                isSelected(day) ? 'selected' : ''
              } ${
                isDisabled(day) ? 'disabled' : ''
              }`}
              onClick={() => handleDateClick(day)}
              disabled={isDisabled(day)}
            >
              {day}
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

export default CustomDatePicker;

